import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { getSubStatusColor } from '../../utils/helpers';
import ScannerModal from './ScannerModal';

const formatNumber = (num) => Number(num).toLocaleString('en-US');

const warehouseMap = {
    EBL_WH: 'Erbil Warehouse',
    DUH_WH: 'Dohuk Warehouse',
    SUL_WH: 'Suly Warehouse',
    KRK_WH: 'Kirkuk Warehouse',
    MSL_WH: 'Mosul Warehouse',
    BGD_WH: 'Baghdad Warehouse',
};

const getAvailableStatuses = (currentStatus) => {
    const statusSequence = ['', 'IN_PROGRESS', 'PREPARED', 'DELIVERED'];
    const currentIndex = statusSequence.indexOf(currentStatus || '');
    const available = statusSequence.slice(currentIndex);
    if (currentStatus && currentStatus !== '') {
        return available.filter(option => option !== '');
    }
    return available;
};

const EditScratchOrdersModal = ({ isOpen, onClose, scratchCardOrder, isEmployee, onSave, isSaving, error: parentError }) => {
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [subStatus, setSubStatus] = useState('');
    const [validationError, setValidationError] = useState('');
    const [shouldShake, setShouldShake] = useState(false);
    
    // Scanner state
    const [isScannerOpen, setIsScannerOpen] = useState(false);
    const [scannerTarget, setScannerTarget] = useState(null);

    useEffect(() => {
        if (isOpen && scratchCardOrder) {
            setFrom(scratchCardOrder.from !== undefined && scratchCardOrder.from !== null ? String(scratchCardOrder.from) : '');
            setTo(scratchCardOrder.to !== undefined && scratchCardOrder.to !== null ? String(scratchCardOrder.to) : '');
            setSubStatus(scratchCardOrder.subStatus || '');
            setValidationError('');
        }
    }, [isOpen, scratchCardOrder]);

    const handleOpenScanner = (target) => {
        setScannerTarget(target);
        setIsScannerOpen(true);
    };

    const handleScanSuccess = (decodedText) => {
        // Restrict to numeric characters
        const cleanValue = decodedText.replace(/[^0-9]/g, '');
        if (scannerTarget === 'from') {
            setFrom(cleanValue);
        } else if (scannerTarget === 'to') {
            setTo(cleanValue);
        }
        setIsScannerOpen(false);
        setScannerTarget(null);
    };

    const handleFromChange = (value) => {
        const cleanValue = value.replace(/[^0-9]/g, '');
        setFrom(cleanValue);
    };

    const handleToChange = (value) => {
        const cleanValue = value.replace(/[^0-9]/g, '');
        setTo(cleanValue);
    };

    const validateForm = () => {
        if (from === '' && to === '') {
            return '';
        }

        if (from === '' || to === '') {
            return 'Both "From" and "To" fields must be filled to specify a range';
        }

        const fromValue = parseInt(from, 10);
        const toValue = parseInt(to, 10);

        if (isNaN(fromValue) || isNaN(toValue)) {
            return 'Please enter valid numbers for "From" and "To"';
        }

        if (fromValue > toValue) {
            return '"To" must be greater than or equal to "From"';
        }

        const count = toValue - fromValue + 1;
        const quantity = parseInt(scratchCardOrder.quantity, 10) || 0;

        if (count !== quantity) {
            return `The quantity of cards between "From" and "To" (${count}) must match the order quantity (${quantity})`;
        }

        return '';
    };

    const handleConfirm = () => {
        const errorMsg = validateForm();
        if (errorMsg) {
            setValidationError(errorMsg);
            setShouldShake(true);
            setTimeout(() => setShouldShake(false), 500);
            return;
        }
        setValidationError('');
        onSave(scratchCardOrder.id, {
            from: from || null,
            to: to || null,
            subStatus: subStatus || null,
        });
    };

    if (!scratchCardOrder) return null;

    const originalStatus = scratchCardOrder.subStatus || '';
    const isDelivered = originalStatus === 'DELIVERED';
    const availableStatuses = getAvailableStatuses(originalStatus);

    const fromVal = parseInt(from, 10);
    const toVal = parseInt(to, 10);
    const hasValidRange = !isNaN(fromVal) && !isNaN(toVal) && fromVal <= toVal;
    const rangeCount = hasValidRange ? toVal - fromVal + 1 : 0;
    const expectedQuantity = parseInt(scratchCardOrder.quantity, 10) || 0;

    const isEmpty = !from && !to;
    
    // Determine status classes
    let containerClass = '';
    let badgeClass = '';
    let badgeText = '';
    let messageText = '';
    
    if (isEmpty) {
        containerClass = 'bg-gray-50 border-gray-200 text-gray-500';
        badgeClass = 'bg-gray-200 text-gray-600';
        badgeText = 'Empty';
        messageText = `Card count: 0 of ${formatNumber(expectedQuantity)} entered`;
    } else if (!hasValidRange) {
        containerClass = 'bg-red-50 border-red-200 text-red-800';
        badgeClass = 'bg-red-200 text-red-950';
        badgeText = 'Mismatch';
        messageText = (from === '' || to === '') 
            ? 'Both "From" and "To" fields must be filled to specify a range' 
            : 'Invalid serial range ("To" must be greater than or equal to "From")';
    } else if (rangeCount === expectedQuantity) {
        containerClass = 'bg-green-50 border-green-200 text-green-800';
        badgeClass = 'bg-green-200 text-green-950';
        badgeText = 'Match';
        messageText = `Card count: ${formatNumber(rangeCount)} of ${formatNumber(expectedQuantity)} entered`;
    } else {
        containerClass = 'bg-red-50 border-red-200 text-red-800';
        badgeClass = 'bg-red-200 text-red-950';
        badgeText = 'Mismatch';
        messageText = `Card count: ${formatNumber(rangeCount)} of ${formatNumber(expectedQuantity)} entered`;
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="md" className="max-w-md">
            <div className="p-1 sm:p-2">
                <h3 className="text-xl font-bold text-gray-900 mb-5">Edit Scratch Order</h3>

                {/* Info Card */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6 shadow-sm">
                    <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                        <div>
                            <span className="text-gray-500 block text-xs mb-0.5">Warehouse</span>
                            <span className="font-semibold text-gray-800">{warehouseMap[scratchCardOrder.warehouse] || scratchCardOrder.warehouse}</span>
                        </div>
                        <div>
                            <span className="text-gray-500 block text-xs mb-0.5">Denomination</span>
                            <span className="font-bold text-gray-900 tabular-nums">IQD {formatNumber(scratchCardOrder.denomination)}</span>
                        </div>
                        <div>
                            <span className="text-gray-500 block text-xs mb-0.5">Quantity</span>
                            <span className="font-semibold text-gray-800 tabular-nums">{scratchCardOrder.quantity}</span>
                        </div>
                        <div>
                            <span className="text-gray-500 block text-xs mb-0.5">Current Sub Status</span>
                            <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full mt-0.5 ${getSubStatusColor(originalStatus)}`}>
                                {originalStatus || '-'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Form Inputs Stacked Vertically */}
                <div className="space-y-4 mb-6">
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">From</label>
                        <div className="relative flex items-center">
                            <input
                                type="text"
                                value={from}
                                onChange={(e) => handleFromChange(e.target.value)}
                                className="w-full pl-3 pr-10 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white tabular-nums shadow-sm"
                                placeholder="Enter starting card number"
                            />
                            <button
                                type="button"
                                onClick={() => handleOpenScanner('from')}
                                className="absolute right-2.5 text-gray-400 hover:text-blue-500 transition-colors p-1"
                                title="Scan starting number barcode/QR code"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7V5a2 2 0 012-2h2m10 0h2a2 2 0 012 2v2m0 10v2a2 2 0 01-2 2h-2M7 21H5a2 2 0 01-2-2v-2M12 7v10m-4-8v6m8-6v6" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">To</label>
                        <div className="relative flex items-center">
                            <input
                                type="text"
                                value={to}
                                onChange={(e) => handleToChange(e.target.value)}
                                className="w-full pl-3 pr-10 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white tabular-nums shadow-sm"
                                placeholder="Enter ending card number"
                            />
                            <button
                                type="button"
                                onClick={() => handleOpenScanner('to')}
                                className="absolute right-2.5 text-gray-400 hover:text-blue-500 transition-colors p-1"
                                title="Scan ending number barcode/QR code"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7V5a2 2 0 012-2h2m10 0h2a2 2 0 012 2v2m0 10v2a2 2 0 01-2 2h-2M7 21H5a2 2 0 01-2-2v-2M12 7v10m-4-8v6m8-6v6" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    <div className={`p-3 rounded-md text-xs font-semibold border flex items-center justify-between transition-all shadow-sm ${
                        shouldShake ? 'animate-shake' : ''
                    } ${containerClass}`}>
                        <div className="flex items-center gap-1.5">
                            <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 00-2 2z" />
                            </svg>
                            <span className="tabular-nums">
                                {messageText}
                            </span>
                        </div>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${badgeClass}`}>
                            {badgeText}
                        </span>
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Sub Status</label>
                        {isEmployee || isDelivered ? (
                            <div className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-600 font-medium flex items-center justify-between shadow-sm">
                                <span>{subStatus || originalStatus || '-'}</span>
                                <span className="text-xs text-gray-400 font-normal">(Non-editable)</span>
                            </div>
                        ) : (
                            <select
                                value={subStatus}
                                onChange={(e) => setSubStatus(e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white font-medium shadow-sm"
                            >
                                {!originalStatus && <option value="">Select status</option>}
                                {availableStatuses.map(status => (
                                    status && <option key={status} value={status}>{status}</option>
                                ))}
                            </select>
                        )}
                    </div>
                </div>

                {parentError && (
                    <div className="mb-6 p-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md">
                        {parentError}
                    </div>
                )}

                <div className="flex justify-end gap-3 mt-4">
                    <button
                        onClick={onClose}
                        disabled={isSaving}
                        className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md text-sm font-medium transition-colors disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={isSaving}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center min-w-[120px]"
                    >
                        {isSaving ? (
                            <>
                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                                Saving...
                            </>
                        ) : 'Save Changes'}
                    </button>
                </div>
            </div>
            
            <ScannerModal
                isOpen={isScannerOpen}
                onClose={() => {
                    setIsScannerOpen(false);
                    setScannerTarget(null);
                }}
                onScanSuccess={handleScanSuccess}
            />
        </Modal>
    );
};

export default EditScratchOrdersModal;
